/**
 * useCADState — owns every piece of React state for the CAD editor.
 *
 * Split from DPL Jade.jsx so that useCADHandlers, CADCanvas, and future
 * v8 hooks (use3DState, useDXFExport …) can all consume the same state
 * without touching DPL Jade.jsx itself.
 *
 * Returns a flat object — every field destructured directly in DPL Jade.
 */
import { useState, useRef, useMemo, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { buildGrid }    from "../engine/spatialGrid";
import { mkEmpty }      from "../engine/dcel";
import { getProjectBOQ } from "../engine/metrics";
import { useHistory }   from "./useHistory";
import { useSave }      from "./useSave";
import { useAuth }      from "./useAuth";
import { useSubscription } from "./useSubscription";
import { UNIT_PRESETS, DISTANCE_UNITS, AREA_UNITS } from "../utils/units";

// ── Stable session project ID ─────────────────────────────────────────────────
// Persisted to localStorage so the same project reloads across browser sessions.
// In a full multi-project dashboard (v8) this will come from the URL / route.
const SESSION_PROJECT_ID = (() => {
  const key = "dpl-jade:current-project-id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = uuidv4();
    localStorage.setItem(key, id);
  }
  return id;
})();

export { SESSION_PROJECT_ID };

export function useCADState() {
  // ── DCEL history ─────────────────────────────────────────────────────────────
  const {
    state: st, commit, beginDrag, commitDrag,
    undo, redo, canUndo, canRedo,
  } = useHistory(mkEmpty());

  // ── Auth / save ───────────────────────────────────────────────────────────────
  const { user }           = useAuth();
  const { plan, isPro }    = useSubscription(user?.id);
  const saveStatus         = useSave(st, SESSION_PROJECT_ID, user?.id, plan);

  // ── Hydration ─────────────────────────────────────────────────────────────────
  const [hydrated, setHydrated] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const svgRef    = useRef(null);
  const panRef    = useRef(null);
  const isPanning = useRef(false);
  const lastClick = useRef(0);
  const snapRef   = useRef(null); // always-fresh snap — avoids stale closure

  // ── Viewport ──────────────────────────────────────────────────────────────────
  const [pan,     setPan]     = useState({ x: 40, y: 60 });
  const [zoom,    setZoom]    = useState(1);
  const [svgSize, setSvgSize] = useState({ w: 800, h: 600 });

  // ── Tool mode ─────────────────────────────────────────────────────────────────
  const [mode,   setMode]   = useState("wall");
  const [matId,  setMatId]  = useState("BRICK");
  const [wallH,  setWallH]  = useState(3000);

  // ── Draw state ────────────────────────────────────────────────────────────
  const [lineType, setLineType] = useState("wall"); // "wall" | "line" | "rect" | "circle"
  const [offsetDist, setOffsetDist] = useState(500);  // mm — default 500mm offset distance
  const [offsetSide, setOffsetSide] = useState("left"); // "left" | "right"

  // ── Transform state (rotate / scale / mirror / array) ─────────────────────────
  const [transformAngle,    setTransformAngle]    = useState(90);    // degrees
  const [transformScale,    setTransformScale]    = useState(1.0);   // factor
  const [arrayRows,         setArrayRows]         = useState(2);
  const [arrayCols,         setArrayCols]         = useState(2);
  const [arrayRowSpacing,   setArrayRowSpacing]   = useState(3000);  // mm
  const [arrayColSpacing,   setArrayColSpacing]   = useState(3000);  // mm
  const [arrayCount,        setArrayCount]        = useState(4);     // polar count
  const [arrayTotalAngle,   setArrayTotalAngle]   = useState(360);   // polar arc
  const [mirrorAxisStart,   setMirrorAxisStart]   = useState(null);  // {x,y} first click
  const [showTransformPanel,setShowTransformPanel]= useState(false);
  // Angular dimension — 3-click: vertex, ptA, ptB
  const [adimPts,           setAdimPts]           = useState([]);
  // Trim mode
  const [trimTarget,        setTrimTarget]        = useState(null);

  // ── Primitive drawing state ────────────────────────────────────────────────
  // circle: [center] then drag to set radius
  // arc: [pt1, pt2] then click pt3
  // spline/polyline: accumulating pts array
  const [primPts,  setPrimPts]  = useState([]);  // click points being accumulated
  const [primType, setPrimType] = useState(null); // "circle"|"arc"|"spline"|"polyline"
  // Primitive draw state
  const [primDrawPts,       setPrimDrawPts]       = useState([]);  // points accumulated while drawing
  const [primDrawType,      setPrimDrawType]       = useState(null); // "circle"|"arc3"|"spline"|"polyline"
  const [offsetHoverEdge, setOffsetHoverEdge] = useState(null); // edge id being hovered in offset mode────
  const [wStart,   setWStart]   = useState(null);
  const [snap,     setSnap]     = useState(null);
  const [snapLock, setSnapLock] = useState(null);

  // ── Selection ─────────────────────────────────────────────────────────────────
  const [sel, setSel] = useState({
    face: null, edge: null, node: null, opening: null, symbol: null, primitive: null,
  });
  // Primitive selection — { type: "circle"|"arc"|"spline"|"polyline", id }
  const [selPrim, setSelPrim] = useState(null);
  // Multi-select — rubber-band selection box
  const [selMany,    setSelMany]    = useState({ edges:[], nodes:[], symbols:[], annotations:[] });
  const [selBox,     setSelBox]     = useState(null);  // { x0,y0,x1,y1 } in world-mm while dragging
  const [selBoxAnchor, setSelBoxAnchor] = useState(null); // { x,y } drag start
  // Symbol drag-after-placement
  const [dragSymbol, setDragSymbol] = useState(null); // symbol id
  const [dragSymPos, setDragSymPos] = useState(null); // { x,y } world mm
  // Annotation drag
  const [dragAnn,    setDragAnn]    = useState(null); // "ann_id"
  const [dragAnnPos, setDragAnnPos] = useState(null); // { x,y } world mm
  const clearSel = useCallback(() =>
    setSel({ face: null, edge: null, node: null, opening: null, symbol: null, primitive: null }),
  [setSel]);
  const [selAnnotation, setSelAnnotation] = useState(null); // "ann_id" | "dim_id" | gl.id

  // ── Drag ──────────────────────────────────────────────────────────────────────
  const [dragNode,  setDragNode]  = useState(null);   // single-node drag id
  const [dragPosMm, setDragPosMm] = useState(null);   // single-node live world pos
  // Multi-node drag — drag all selected nodes together
  const [dragNodes,  setDragNodes]  = useState(null);  // [{ id, x0, y0 }] start positions
  const [dragAnchor, setDragAnchor] = useState(null);  // { x, y } world mm at drag start
  const [dragDelta,  setDragDelta]  = useState({ dx:0, dy:0 }); // live offset

  // ── UI toggles ────────────────────────────────────────────────────────────
  const [isFullscreen,    setIsFullscreen]    = useState(false);
  const [showCmdLine,     setShowCmdLine]     = useState(true);
  const [showMenuBar,     setShowMenuBar]     = useState(true);
  const [activeMenu,      setActiveMenu]      = useState(null); // "file"|"edit"|"view"|null────
  const [showHE,           setShowHE]           = useState(false);
  const [tab,              setTab]              = useState("props");
  const [editingDim,       setEditingDim]       = useState(null);
  const [showHealLog,      setShowHealLog]      = useState(false);
  const [showProjectEditor,setShowProjectEditor]= useState(false);
  const [showTemplates,    setShowTemplates]    = useState(false);
  const [showDrawing,      setShowDrawing]      = useState(false);
  const [showLayerPanel,   setShowLayerPanel]   = useState(true);

  // ── Popups ────────────────────────────────────────────────────────────────────
  const [roomPopup,    setRoomPopup]    = useState(null);
  const [openingPopup, setOpeningPopup] = useState(null);

  // ── Layer / symbol ────────────────────────────────────────────────────────────
  const [activeLayer,  setActiveLayer]  = useState("walls");
  const [activeSymbol, setActiveSymbol] = useState(null);

  // ── Annotation inline input ───────────────────────────────────────────────────
  const [pendingAnnotation, setPendingAnnotation] = useState(null); // { x, y } world mm
  const [annotationDraft,   setAnnotationDraft]   = useState("");
  const [pendingGridLine,   setPendingGridLine]   = useState(null); // { x, y } world mm
  const [activeSectionCut, setActiveSectionCut] = useState(null); // cut id currently shown in DrawingView

  // ── Unit preferences ──────────────────────────────────────────────────────────
  const [unitPreset,  setUnitPreset]  = useState(UNIT_PRESETS.DEFAULT);
  const [distUnit, setDistUnit] = useState(DISTANCE_UNITS.M);
  const [areaUnit, setAreaUnit] = useState(AREA_UNITS.M2);

  // ── Derived / expensive ───────────────────────────────────────────────────────
  const grid = useMemo(() => buildGrid(st), [st]);
  const boq  = useMemo(() => getProjectBOQ(st), [st]);

  // ── Derived flags ─────────────────────────────────────────────────────────────
  const errors = st._errors || [];
  const hasErr = errors.length > 0;

  return {
    // DCEL + history
    st, commit, beginDrag, commitDrag,
    undo, redo, canUndo, canRedo,

    // Auth / save
    user, plan, isPro, saveStatus,

    // Hydration
    hydrated, setHydrated,

    // Refs (stable identity — safe to spread into handlers)
    svgRef, panRef, isPanning, lastClick, snapRef,

    // Viewport
    pan, setPan,
    zoom, setZoom,
    svgSize, setSvgSize,

    // Tool mode
    mode, setMode,
    matId, setMatId,
    wallH, setWallH,

    // Draw state
    lineType, setLineType,
    offsetDist, setOffsetDist,
    offsetSide, setOffsetSide,
    offsetHoverEdge, setOffsetHoverEdge,

    // Transform
    transformAngle,    setTransformAngle,
    transformScale,    setTransformScale,
    arrayRows,         setArrayRows,
    arrayCols,         setArrayCols,
    arrayRowSpacing,   setArrayRowSpacing,
    arrayColSpacing,   setArrayColSpacing,
    arrayCount,        setArrayCount,
    arrayTotalAngle,   setArrayTotalAngle,
    mirrorAxisStart,   setMirrorAxisStart,
    showTransformPanel,setShowTransformPanel,
    adimPts,           setAdimPts,
    trimTarget,        setTrimTarget,
    primPts,  setPrimPts,
    primType, setPrimType,
    selPrim,           setSelPrim,
    primDrawPts,       setPrimDrawPts,
    primDrawType,      setPrimDrawType,
    wStart,   setWStart,
    snap,     setSnap,
    snapLock, setSnapLock,

    // Selection
    clearSel,
    sel, setSel,
    selMany, setSelMany,
    selBox,  setSelBox,
    selBoxAnchor, setSelBoxAnchor,
    // Symbol drag
    dragSymbol, setDragSymbol,
    dragSymPos, setDragSymPos,
    // Annotation drag
    dragAnn,    setDragAnn,
    dragAnnPos, setDragAnnPos,
    selAnnotation, setSelAnnotation,

    // Drag
    dragNode,  setDragNode,
    dragPosMm, setDragPosMm,
    dragNodes,  setDragNodes,
    dragAnchor, setDragAnchor,
    dragDelta,  setDragDelta,

    // UI toggles
    isFullscreen, setIsFullscreen,
    showCmdLine,  setShowCmdLine,
    showMenuBar,  setShowMenuBar,
    activeMenu,   setActiveMenu,
    showHE, setShowHE,
    tab, setTab,
    editingDim, setEditingDim,
    showHealLog,       setShowHealLog,
    showProjectEditor, setShowProjectEditor,
    showTemplates,     setShowTemplates,
    showDrawing,       setShowDrawing,
    showLayerPanel,    setShowLayerPanel,

    // Popups
    roomPopup,    setRoomPopup,
    openingPopup, setOpeningPopup,

    // Layer / symbol
    activeLayer,  setActiveLayer,
    activeSymbol, setActiveSymbol,

    // Annotation inline input
    pendingAnnotation, setPendingAnnotation,
    annotationDraft,   setAnnotationDraft,
    pendingGridLine,   setPendingGridLine,
    activeSectionCut,  setActiveSectionCut,

    // Unit preferences
    unitPreset,  setUnitPreset,
    distUnit, setDistUnit,
    areaUnit, setAreaUnit,

    // Derived
    grid, boq,
    errors, hasErr,
  };
}
